/**
 * User Tracking Service for FishGPT Frontend
 * 
 * Provides comprehensive user interaction tracking including:
 * - Page views and navigation
 * - User interactions (clicks, scrolls, etc.)
 * - Feature usage tracking
 * - Error logging
 * - Performance monitoring
 */

import React from 'react';

class UserTrackingService {
    constructor() {
        this.sessionId = null;
        this.eventQueue = [];
        this.batchSize = 20;
        this.batchTimeout = 30000; // 30 seconds
        this.batchTimer = null;
        this.isOnline = navigator.onLine;
        this.pageStartTime = Date.now();
        this.scrollDepth = 0;
        this.maxScrollDepth = 0;
        this.isTrackingEnabled = true;
        this.currentPage = window.location.pathname;
        this.pageTitle = document.title;
        
        this.initializeTracking();
        this.setupEventListeners();
    }
    
    initializeTracking() {
        // Get or create session ID
        this.sessionId = this.getSessionId();
        
        // Set session ID header for all requests
        this.setDefaultHeaders();
        
        // Track initial page view
        this.trackPageView(window.location.pathname, document.title);
        
        // Setup batch processing
        this.startBatchTimer();
    }
    
    getSessionId() {
        let sessionId = sessionStorage.getItem('user_session_id');
        if (!sessionId) {
            sessionId = this.generateUUID();
            sessionStorage.setItem('user_session_id', sessionId);
        }
        return sessionId;
    }
    
    generateUUID() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }
    
    setDefaultHeaders() {
        // Store session ID for use in fetch requests
        this.defaultHeaders = {
            'Content-Type': 'application/json',
            'X-Session-ID': this.sessionId
        };
        
        // Add auth token if available
        const authToken = localStorage.getItem('access_token');
        if (authToken) {
            this.defaultHeaders['Authorization'] = `Bearer ${authToken}`;
        }
    }
    
    setupEventListeners() {
        // Online/offline status
        window.addEventListener('online', () => {
            this.isOnline = true;
            this.flushQueue();
        });
        
        window.addEventListener('offline', () => {
            this.isOnline = false;
        });
        
        // Page unload - flush remaining events
        window.addEventListener('beforeunload', () => {
            this.trackPageExit();
            this.flushQueue(true); // Synchronous flush
        });
        
        // Visibility change - track when user switches tabs
        /* Disabling to reduce log volume as per user request. 
           Page exit metrics will now only be captured on final unload.
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                this.trackPageExit();
            } else {
                this.pageStartTime = Date.now();
            }
        });
        */
        
        // Scroll tracking
        let scrollTimeout;
        window.addEventListener('scroll', () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
                this.updateScrollDepth();
            }, 100);
        });
        
        // Click tracking
        /* Disabling generic click tracking to reduce log volume.
           Specific, important interactions are tracked via dedicated functions 
           like trackChatInteraction() from the components themselves.
        document.addEventListener('click', (event) => {
            this.trackClick(event);
        }, true);
        */
        
        // Form submissions
        document.addEventListener('submit', (event) => {
            this.trackFormSubmission(event);
        }, true);
        
        // Error tracking
        window.addEventListener('error', (event) => {
            this.trackError('javascript_error', event.message, event.error?.stack, {
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
                page: window.location.pathname
            });
        });
        
        // Unhandled promise rejections
        window.addEventListener('unhandledrejection', (event) => {
            this.trackError('unhandled_promise_rejection', event.reason?.message || 'Unhandled promise rejection', 
                event.reason?.stack, {
                page: window.location.pathname
            });
        });
    }
    
    updateScrollDepth() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        
        if (docHeight > 0) {
            this.scrollDepth = Math.round((scrollTop / docHeight) * 100);
            this.maxScrollDepth = Math.max(this.maxScrollDepth, this.scrollDepth);
        }
    }
    
    trackPageView(pagePath, pageTitle = null, referrerPath = null) {
        if (!this.isTrackingEnabled) return;
        
        const event = {
            type: 'page_view',
            page_path: pagePath,
            page_title: pageTitle || document.title,
            referrer_path: referrerPath || document.referrer,
            viewport_width: window.innerWidth,
            viewport_height: window.innerHeight,
            timestamp: Date.now()
        };
        
        this.queueEvent(event);
        
        // Reset page tracking variables
        this.pageStartTime = Date.now();
        this.maxScrollDepth = 0;
        this.currentPage = pagePath;
        this.pageTitle = pageTitle || document.title;
    }
    
    trackPageExit() {
        if (!this.isTrackingEnabled) return;
        
        const timeOnPage = Math.round((Date.now() - this.pageStartTime) / 1000);
        
        // Update the last page view with time on page and scroll depth
        const event = {
            type: 'page_view',
            page_path: this.currentPage,
            page_title: this.pageTitle,
            time_on_page_seconds: timeOnPage,
            scroll_depth_percentage: this.maxScrollDepth,
            exit_type: 'navigation'
        };
        
        this.queueEvent(event);
    }
    
    trackClick(event) {
        if (!this.isTrackingEnabled) return;
        
        const element = event.target;
        const elementInfo = this.getElementInfo(element);
        
        const clickEvent = {
            type: 'user_interaction',
            interaction_type: 'click',
            element_type: element.tagName?.toLowerCase(),
            element_id: elementInfo.id,
            element_class: elementInfo.classes,
            element_text: elementInfo.text,
            page_path: window.location.pathname,
            interaction_metadata: {
                x: event.clientX,
                y: event.clientY,
                timestamp: Date.now()
            }
        };
        
        this.queueEvent(clickEvent);
    }
    
    trackFormSubmission(event) {
        if (!this.isTrackingEnabled) return;
        
        const form = event.target;
        const elementInfo = this.getElementInfo(form);
        
        const formEvent = {
            type: 'user_interaction',
            interaction_type: 'form_submit',
            element_type: 'form',
            element_id: elementInfo.id,
            element_class: elementInfo.classes,
            page_path: window.location.pathname,
            interaction_metadata: {
                action: form.action,
                method: form.method,
                timestamp: Date.now()
            }
        };
        
        this.queueEvent(formEvent);
    }
    
    trackFeatureUsage(featureName, metadata = {}, durationSeconds = null, completionStatus = null) {
        if (!this.isTrackingEnabled) return;
        
        const event = {
            type: 'feature_usage',
            feature_name: featureName,
            feature_metadata: {
                ...metadata,
                page: window.location.pathname,
                timestamp: Date.now()
            },
            duration_seconds: durationSeconds,
            completion_status: completionStatus
        };
        
        this.queueEvent(event);
    }
    
    trackError(errorType, errorMessage, errorStack = null, errorContext = {}) {
        if (!this.isTrackingEnabled) return;
        
        const event = {
            type: 'error',
            error_type: errorType,
            error_message: errorMessage,
            error_stack: errorStack,
            error_context: {
                ...errorContext,
                page: window.location.pathname,
                user_agent: navigator.userAgent,
                timestamp: Date.now()
            },
            severity: this.determineErrorSeverity(errorType, errorMessage)
        };
        
        // Send errors immediately (don't queue)
        this.sendEvent(event, '/api/logging/error');
    }
    
    determineErrorSeverity(errorType, errorMessage) {
        if (errorType.includes('critical') || errorMessage.includes('critical')) {
            return 'critical';
        }
        if (errorType.includes('warning') || errorMessage.includes('warning')) {
            return 'warning';
        }
        return 'error';
    }
    
    getElementInfo(element) {
        return {
            id: element.id || null,
            classes: element.className || null,
            text: this.getElementText(element)
        };
    }
    
    getElementText(element) {
        if (!element) return null;
        
        // For buttons, links, and other interactive elements, get meaningful text
        if (element.tagName === 'BUTTON' || element.tagName === 'A') {
            return element.textContent?.trim().substring(0, 100) || null;
        }
        
        // For inputs, get the placeholder or label
        if (element.tagName === 'INPUT') {
            return element.placeholder || element.value?.substring(0, 50) || null;
        }
        
        // For other elements, get limited text content
        const text = element.textContent?.trim();
        return text && text.length > 0 ? text.substring(0, 50) : null;
    }
    
    queueEvent(event) {
        this.eventQueue.push(event);
        
        // Send immediately if queue is full
        if (this.eventQueue.length >= this.batchSize) {
            this.flushQueue();
        }
    }
    
    startBatchTimer() {
        if (this.batchTimer) {
            clearTimeout(this.batchTimer);
        }
        
        this.batchTimer = setTimeout(() => {
            this.flushQueue();
            this.startBatchTimer(); // Restart timer
        }, this.batchTimeout);
    }
    
    async flushQueue(synchronous = false) {
        if (this.eventQueue.length === 0 || !this.isOnline) return;
        
        const events = [...this.eventQueue];
        this.eventQueue = [];
        
        if (synchronous) {
            // Use sendBeacon for synchronous sending (e.g., on page unload)
            try {
                const data = JSON.stringify({ events });
                navigator.sendBeacon('/api/logging/batch', data);
            } catch (error) {
                console.warn('Failed to send tracking data via beacon:', error);
            }
        } else {
            // Use regular fetch for asynchronous sending
            try {
                await fetch('/api/logging/batch', {
                    method: 'POST',
                    headers: this.defaultHeaders,
                    body: JSON.stringify({ events })
                });
            } catch (error) {
                console.warn('Failed to send tracking data:', error);
                // Re-queue events if sending failed and we're online
                if (this.isOnline) {
                    this.eventQueue.unshift(...events.slice(-10)); // Keep last 10 events
                }
            }
        }
    }
    
    async sendEvent(eventData, endpoint) {
        if (!this.isOnline) return;
        
        try {
            await fetch(endpoint, {
                method: 'POST',
                headers: this.defaultHeaders,
                body: JSON.stringify(eventData)
            });
        } catch (error) {
            console.warn(`Failed to send event to ${endpoint}:`, error);
        }
    }
    
    // Public API methods
    enable() {
        this.isTrackingEnabled = true;
    }
    
    disable() {
        this.isTrackingEnabled = false;
        this.eventQueue = [];
    }
    
    updateAuthToken(token) {
        if (token) {
            this.defaultHeaders['Authorization'] = `Bearer ${token}`;
        } else {
            delete this.defaultHeaders['Authorization'];
        }
    }
    
    // React Router integration
    trackRouteChange(location) {
        const previousPage = this.currentPage;
        this.trackPageExit();
        this.trackPageView(location.pathname, document.title, previousPage);
    }
    
    // Feature-specific tracking methods
    trackChatInteraction(action, metadata = {}) {
        this.trackFeatureUsage('chat_interaction', {
            action,
            ...metadata
        });
    }
    
    trackImageUpload(success, fileSize = null, fileType = null, errorMessage = null) {
        this.trackFeatureUsage('image_upload', {
            success,
            file_size: fileSize,
            file_type: fileType,
            error_message: errorMessage
        });
    }
    
    trackSymptomChecker(step, selections = {}) {
        this.trackFeatureUsage('symptom_checker', {
            step,
            selections
        });
    }
    
    trackArticleView(articleSlug, readingTime = null) {
        this.trackFeatureUsage('article_view', {
            article_slug: articleSlug,
            reading_time: readingTime
        });
    }
    
    trackImageExpansion(imageType, diseaseName = null) {
        this.trackFeatureUsage('image_expand', {
            image_type: imageType,
            disease_name: diseaseName
        });
    }
}

// Create global instance
const userTracking = new UserTrackingService();

// React hooks for easy integration
export const useUserTracking = () => {
    // useMemo ensures that the same object with the same bound function
    // references is returned on every render. This prevents unnecessary re-renders
    // in components that use these functions in a useEffect dependency array,
    // which was the likely cause of the page freezing and excessive logging.
    return React.useMemo(() => ({
        trackFeatureUsage: userTracking.trackFeatureUsage.bind(userTracking),
        trackChatInteraction: userTracking.trackChatInteraction.bind(userTracking),
        trackImageUpload: userTracking.trackImageUpload.bind(userTracking),
        trackSymptomChecker: userTracking.trackSymptomChecker.bind(userTracking),
        trackArticleView: userTracking.trackArticleView.bind(userTracking),
        trackImageExpansion: userTracking.trackImageExpansion.bind(userTracking),
        trackError: userTracking.trackError.bind(userTracking),
        updateAuthToken: userTracking.updateAuthToken.bind(userTracking),
        trackRouteChange: userTracking.trackRouteChange.bind(userTracking)
    }), []);
};

// Higher-Order Component for automatic tracking
export const withUserTracking = (WrappedComponent, featureName) => {
    return function TrackedComponent(props) {
        React.useEffect(() => {
            const startTime = Date.now();
            
            userTracking.trackFeatureUsage(featureName, {}, null, 'started');
            
            return () => {
                const duration = Math.round((Date.now() - startTime) / 1000);
                userTracking.trackFeatureUsage(featureName, {}, duration, 'completed');
            };
        }, []);
        
        return React.createElement(WrappedComponent, props);
    };
};

// Error boundary integration
export class TrackingErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    
    static getDerivedStateFromError(error) {
        return { hasError: true };
    }
    
    componentDidCatch(error, errorInfo) {
        userTracking.trackError('react_error_boundary', error.message, error.stack, {
            component_stack: errorInfo.componentStack,
            error_boundary: this.props.name || 'unknown'
        });
    }
    
    render() {
        if (this.state.hasError) {
            return this.props.fallback || React.createElement('div', {}, 'Something went wrong.');
        }
        
        return this.props.children;
    }
}

export default userTracking; 